import {
  Range,
  Editor,
  Point,
  Node,
  Text,
  Element,
  NodeEntry,
  Path,
  Transforms,
} from 'domains/reporter/RichTextEditor/core';
import { PUNCTUATIONS } from '../stitching/normalizationHelpers';
import { PICKLIST_PLUGIN_ID, INLINE_BOOKMARK_PLUGIN_ID } from '../plugins';
import clone from 'ramda/es/clone';
import { cloneDeep } from 'lodash';
import { ReactEditor } from 'slate-react';
import { PARAGRAPH_PLUGIN_ID } from '../plugins/paragraph/types';
import { HEADING_PLUGIN_ID } from '../plugins/heading/types';

type IndexCheckProps = {
  text: string;
  characters: string[];
  last?: boolean;
};

// helper function that returns the nearest offset of a break character so we can expand a selection
const indexOfs = ({ text, characters, last }: IndexCheckProps) => {
  const indices = characters
    .map((character) => (last === true ? text.lastIndexOf(character) : text.indexOf(character)))
    //remove -1's for characters that arent found
    .filter((index) => index !== -1);

  if (!indices.length) return undefined;
  return last === true ? Math.max(...indices) : Math.min(...indices);
};

export const getStartAndEndText = (
  editor: Editor,
  start: Point,
  end: Point
):
  | {
      startText: string;
      endText: string;
    }
  | undefined => {
  const selection = editor.selection;
  if (selection == null) return;

  // Node.get throws if the path no longer exists (e.g. a stale selection
  // against an empty/partially-initialized editor), so guard before reading.
  if (!Node.has(editor, start.path) || !Node.has(editor, end.path)) return;

  const startType = Node.get(editor, start.path);
  if (!Text.isText(startType)) return;
  const { text: startText } = startType as Text;

  const endType = Node.get(editor, end.path);
  const endText = (endType as Text)?.text;
  if (!Text.isText(endType)) return;

  if (startText === null || endText === null) return;

  return { startText, endText };
};

const breakCharacters = [' ', '\n', ...PUNCTUATIONS.split('')];

export const getOffsetChanges = ({
  start,
  end,
  endText,
  beforeToStartOffset,
  endOffsetToAfter,
}: {
  start: Point;
  end: Point;
  endText: string;
  beforeToStartOffset: string;
  endOffsetToAfter: string;
}): {
  startOffsetChange: number;
  endOffsetChange: number;
} => {
  // grabs where the closest breakCharacter is, and expands the selection
  const expandedStartOffset = indexOfs({
    last: true,
    text: beforeToStartOffset,
    characters: breakCharacters,
  });

  let startOffsetChange = 0;
  // when there is no space at the front, we are selecting part of the first word in the node, and need to expand all the way
  // this occurs when the user selects a portion of the very first node (theres no spaces or punctuation, but we have to expand all the way)
  if (expandedStartOffset === undefined) {
    startOffsetChange = start.offset;
  } else {
    startOffsetChange = start.offset - (expandedStartOffset + 1);
  }

  // increase selection to the nearest break character
  const endOffsetChange =
    indexOfs({
      text: endOffsetToAfter,
      characters: breakCharacters,
    }) ??
    // when there is no space at the end, we are selecting part of the last word in the node, and need to expand all the way

    endText.length - end.offset;

  return { startOffsetChange, endOffsetChange };
};
/**
 * Given an editor, return a selection to be used for dictation purposes
 *
 * Currently, if a user's selection is collapsed, inside a word, and AFTER the first letter or BEFORE the last letter,
 * return a dictation selection that is at the end of the word
 *
 * `selection` defaults to the live `editor.selection` but can be passed explicitly to snap a
 * snapshotted/queued caret against the current document without depending on where the live caret
 * happens to be — the snap only needs the document text around the point, not `editor.selection`.
 */
export const getSelectionForDictation = (
  editor: Editor,
  selection: Range | null = editor.selection
): Range | null => {
  if (selection == null) {
    return null;
  }

  // return the original selection if not collapsed
  if (!Range.isCollapsed(selection)) {
    return selection;
  }

  const [anchor] = Range.edges(selection);

  // using anchor for both start and end, since selection is collapsed.
  const texts = getStartAndEndText(editor, anchor, anchor);
  if (texts == null) return null;

  const { startText, endText } = texts;

  // gets the content before the selection, up to the start
  const beforeToStartOffset = startText.slice(0, anchor.offset);

  // from the end of the selection, to the end of the entire node
  const endOffsetToAfter = endText.slice(anchor.offset, endText.length);

  if (beforeToStartOffset.trim().endsWith('(') && endOffsetToAfter.trim().startsWith(')')) {
    return null;
  }

  const { startOffsetChange, endOffsetChange } = getOffsetChanges({
    start: anchor,
    end: anchor,
    endText,
    beforeToStartOffset,
    endOffsetToAfter,
  });

  // don't proceed if the cursor is at the start or at the end of the word
  if (startOffsetChange === 0 || endOffsetChange === 0) {
    return null;
  }

  // This is the point at the very end of the word in question
  const newPoint: Point = {
    path: selection.anchor.path,
    offset: selection.anchor.offset + endOffsetChange,
  };

  // If there is a space after the calculated point, return that selection instead.
  const [node] = Editor.node(editor, newPoint.path);
  if (Node.string(node)) {
    const character = Text.isText(node) && node.text[newPoint.offset];

    if (character === ' ') {
      return Editor.range(editor, { ...newPoint, offset: newPoint.offset + 1 });
    }
  }

  return Editor.range(editor, newPoint);
};

export const getEditorDictationSelectionSafe = (editor: Editor): Range => {
  if (editor.selection == null) {
    throw new Error('Cannot get editor selection of null!');
  }

  const selection = getSelectionForDictation(editor) ?? editor.selection;

  if (selection == null) {
    throw new Error('Cannot get editor dictation selection of null!');
  }

  return selection;
};

export const getEditorSelectionSafe = (editor: Editor): Range => {
  if (editor.selection == null) {
    throw new Error('Cannot get editor selection of null!');
  }

  return editor.selection;
};

export const getExpandedPicklistSelection = (editor: Editor): Range | undefined => {
  try {
    const selection = editor.selection;
    if (selection == null) return undefined;

    // Check if anchor is in a picklist
    const anchorPicklistEntry = getActivePicklist(editor, selection.anchor);
    // Check if focus is in a picklist
    const focusPicklistEntry = getActivePicklist(editor, selection.focus);

    // If you are using the field pane in either the macro and template editors, we do not allow you to directly interact with
    // the picklist element in the editor. Rather, you need to edit the options in the pane. As a result, we don't allow you to partially
    // select a picklist. If your selection is expanded and includes only a portion of the picklist, we move your selection to entirely select the picklist (including brackets).
    //
    // Note: Currently, nested fields (picklists within picklists or inline bookmarks) are not supported in template/macro editors,
    // but this function uses getActivePicklist (which handles nested scenarios) to guard against this case for future support.

    // If both anchor and focus are in the same picklist, select the entire picklist
    if (
      anchorPicklistEntry != null &&
      focusPicklistEntry != null &&
      Path.equals(anchorPicklistEntry[1], focusPicklistEntry[1])
    ) {
      const isRangeBackward = Range.isBackward(selection);
      const picklistPath = anchorPicklistEntry[1];
      const startPicklistPoint = Editor.start(editor, picklistPath);
      const endPicklistPoint = Editor.end(editor, picklistPath);

      if (startPicklistPoint && endPicklistPoint != null) {
        return {
          anchor: isRangeBackward ? endPicklistPoint : startPicklistPoint,
          focus: isRangeBackward ? startPicklistPoint : endPicklistPoint,
        };
      }
    }

    // Handle partial intersections - adjust anchor and/or focus to exclude picklists
    if (anchorPicklistEntry != null || focusPicklistEntry != null) {
      const expandedSelection = clone(selection);
      const isRangeBackward = Range.isBackward(selection);

      // If anchor is in a picklist, move it outside
      if (anchorPicklistEntry != null) {
        const anchorPoint = isRangeBackward
          ? Editor.after(editor, anchorPicklistEntry[1])
          : Editor.before(editor, anchorPicklistEntry[1]);
        if (anchorPoint != null) {
          expandedSelection.anchor = anchorPoint;
        }
      }

      // If focus is in a picklist, move it outside
      if (focusPicklistEntry != null) {
        const focusPoint = isRangeBackward
          ? Editor.before(editor, focusPicklistEntry[1])
          : Editor.after(editor, focusPicklistEntry[1]);
        if (focusPoint != null) {
          expandedSelection.focus = focusPoint;
        }
      }

      return expandedSelection;
    }
  } catch (e) {
    return undefined;
  }
};

/**
 * Safely converts a DOM point to a Slate point.
 */
export const toSafeSlatePoint = (
  editor: Editor,
  domContainer: Node,
  domOffset: number
): Point | null => {
  try {
    // Type new slate version requires a different argument type
    return (ReactEditor.toSlatePoint as any)(editor, [domContainer, domOffset], {
      exactMatch: false,
    });
  } catch (e) {
    return null;
  }
};

// any character that is not a word boundary (space, punctuation, line breaks)
export const isWordChar = (char: string) => /[^\s.!?;:,.\\-]/.test(char);

/**
 * Gets the character to the left of the current Slate point
 */
export const getLeftChar = (editor: Editor, point: Point): string => {
  const before = Editor.before(editor, point, { unit: 'offset' });
  if (!before) return '';
  const range = { anchor: before, focus: point };
  return Editor.string(editor, range);
};

/**
 * Gets the character to the right of the current Slate point
 */
export const getRightChar = (editor: Editor, point: Point): string => {
  const after = Editor.after(editor, point, { unit: 'offset' });
  if (!after) return '';
  const range = { anchor: point, focus: after };
  return Editor.string(editor, range);
};

/**
 * Collapses an expanded selection to its end in document order and moves cursor to end of line.
 */
export const moveSelectionToEndOfLine = (editor: Editor) => {
  if (editor.selection != null && !Range.isCollapsed(editor.selection)) {
    const [, end] = Range.edges(editor.selection);
    Transforms.select(editor, end);
  }

  Transforms.move(editor, { distance: 1, unit: 'line' });
};

/**
 * Expands from a Slate point left and right to contain full word
 */
export const getWordRangeAtPoint = (editor: Editor, point: Point): Range => {
  const current = cloneDeep(point);
  let startOffset = point.offset;
  let endOffset = point.offset;

  // Go left until hitting a non-word character
  while (current.offset > 0 && isWordChar(getLeftChar(editor, current))) {
    current.offset--;
    startOffset = current.offset;
  }

  current.offset = point.offset;

  // Go right until hitting a non-word character
  while (isWordChar(getRightChar(editor, current))) {
    current.offset++;
    endOffset = current.offset;
  }

  return {
    anchor: { path: point.path, offset: startOffset },
    focus: { path: point.path, offset: endOffset },
  };
};

/**
 * Expands the selection to encompass the entire paragraph (or heading) block.
 * This spans across all fields, picklists, and text.
 */
export const getParagraphRangeAtPoint = (editor: Editor, point?: Point): Range | undefined => {
  try {
    const targetPoint = point ?? editor.selection?.anchor;
    if (!targetPoint) {
      return undefined;
    }

    // Find the nearest paragraph or heading block containing the point
    const blockEntry = Editor.above(editor, {
      at: targetPoint,
      match: (n) =>
        Element.isElement(n) && (n.type === PARAGRAPH_PLUGIN_ID || n.type === HEADING_PLUGIN_ID),
      mode: 'lowest',
    });

    if (!blockEntry) {
      return undefined;
    }

    const [, blockPath] = blockEntry;

    const start = Editor.start(editor, blockPath);
    const end = Editor.end(editor, blockPath);

    if (!start || !end) {
      return undefined;
    }

    return {
      anchor: start,
      focus: end,
    };
  } catch (e) {
    return undefined;
  }
};

/**
 * Shared helper to find an active element of a specific type at the given selection.
 * Uses Editor.above with mode: 'lowest' to find the innermost element in nested scenarios.
 *
 * @param editor - The Slate editor instance
 * @param at - Optional selection point. Defaults to editor.selection
 * @param elementType - The type of element to find
 * @returns A NodeEntry tuple [node, path] for the active element, or null if none found
 */
const getActiveElementByType = (
  editor: Editor,
  at: Range | Point | null,
  elementType: string
): NodeEntry<Element> | null => {
  if (at == null) {
    return null;
  }

  try {
    const element = Editor.above(editor, {
      at,
      match: (n) => Element.isElement(n) && n.type === elementType,
      mode: 'lowest',
    });

    return element ?? null;
  } catch (error) {
    return null;
  }
};

/**
 * Returns the active picklist (innermost picklist) at the given selection.
 * Uses Editor.above with mode: 'lowest' to find the innermost picklist in nested scenarios.
 *
 * @param editor - The Slate editor instance
 * @param at - Optional selection point. Defaults to editor.selection
 * @returns A NodeEntry tuple [node, path] for the active picklist, or null if none found
 */
export const getActivePicklist = (
  editor: Editor,
  at?: Range | Point | null
): NodeEntry<Element> | null => {
  const selection = at ?? editor.selection;
  return getActiveElementByType(editor, selection, PICKLIST_PLUGIN_ID);
};

/**
 * Returns the active bracket (innermost picklist or inline bookmark) at the given selection.
 * Uses Editor.above with mode: 'lowest' to find the innermost bracket in nested scenarios.
 * Inline bookmarks take precedence over picklists.
 *
 * @param editor - The Slate editor instance
 * @param at - Optional selection point. Defaults to editor.selection
 * @returns A NodeEntry tuple [node, path] for the active bracket, or null if none found
 */
export const getActiveBracket = (
  editor: Editor,
  at?: Range | Point | null
): NodeEntry<Element> | null => {
  const selection = at ?? editor.selection;

  // Find the innermost inline bookmark first (takes precedence)
  const inlineBookmark = getActiveElementByType(editor, selection, INLINE_BOOKMARK_PLUGIN_ID);
  if (inlineBookmark != null) {
    return inlineBookmark;
  }

  // Fall back to picklist
  return getActivePicklist(editor, selection);
};
